import { apiToken } from '@/composables/auth'
import type { Notification } from '@/types/shared/player'
import { type Badge, BADGE_ID, DAILY_TASK_ID, type PlayerActivities, type PlayerStats, type UpgradeEvent } from '@/types/shared/player'
import {
  ECOMMERCE_ITEM_PRICE_TYPE,
  type GiveawayConfig,
  MINI_GAME_ID,
  MYSTERY_BOX_TYPE,
  type MysteryBoxReward,
  type PlayerConfig,
  SPECIAL_ECOMMERCE_ITEM_ID,
} from '@/types/shared/player-config'
import type { Nullable } from '@/types/shared/common'
import type { Friend } from '@/types/referral'
import { callApi } from '@/services/call-api'
import type { FortuneWheelPrize, FortuneWheelPrizeV2, PlayerFortuneWheelState } from '@/types/shared/fortune-wheel'
import type { SettingsNotificationType } from '@/types/shared/settings'
import { PVP_GAME_STATUS, type PVP_GAME_STRATEGY } from '@/types/shared/pvp'
import type { PurchaseMetadata } from '@/composables/game/ecommerce'
import type { CurrencyAmount, LiveOpsCurrency, LiveOpsEventTemplate, LiveOpsLeaderboard, LiveOpsUserEvent } from '@/types/live-ops'

export type AchievementsV1Response = {
  playerStats: PlayerStats,
  totalSpins: number,
  tasks: number,
  cards: {
    upgrades: number,
    special: number,
    above15: number,
    above25: number,
    above50: number,
    above100: number,
    above150?: number,
  },
  inventory: {
    tgeAllocationBoost: number
    bronzeTgeBoostCard: number
    silverTgeBoostCard: number
    goldTgeBoostCard: number
    diamondMultiplier: number
    platinumPass: number
    emberElementShard: number
    frostElementShard: number
    voltElementShard: number
    stoneElementShard: number
    elementMultiplier: number
    rainbowKey: number
    primeSigil: number
    winterCrown: number
  },
  daysSinceOnboarding: number,
  badges: Partial<Record<BADGE_ID, number>>
  currentLevelRank: number | null,
  effortLeaderboardCurrentRank: number | null,
}

type LeaderboardResponse = {
  thisPlayer: Nullable<{ rank: number, score: number }>
  players: { firstName: string, score: number, photoUrl: string, isPremium: boolean, isThisPlayer: boolean }[]
}

type LiveOpsLeaderboardResponse = {
  topUsers: { id: string, rank: number, score: number, displayName: string, photoUrl: string }[]
  currentUser: Nullable<{ rank: number, score: number }>
}

type InvoiceResponse = { isPaid: false, invoiceUrl: string, nonce: string, amount: number } | { isPaid: true }

export class ApiError extends Error {
  constructor(message: string, public readonly metadata?: Record<string, unknown>, public readonly code?: string) {
    super(message)
  }
}

class Api {
  private async fetch<T>(input: {
    path: string,
    body?: object,
    method: 'POST' | 'DELETE' | 'GET'
    useDropeeApps?: boolean
  }): Promise<T> {
    return await callApi({
      ...input,
      path: `/api${input.path}`,
      headers: {
        'Authorization': `Bearer ${apiToken.value}`,
      },
    })
  }

  public async hasDoneDailyTx() {
    const response = await this.fetch<{ success: boolean }>({
      path: '/game/has-done-daily-tx',
      method: 'POST',
    })

    return response.success
  }

  public async getNotificationsCount() {
    return await this.fetch<{ count: number }>({
      path: '/game/notifications/count',
      method: 'GET',
    })
  }

  public async onboardingDone() {
    return await this.fetch<{ playerStats: PlayerStats, isReferred: boolean }>({
      path: '/game/actions/onboarding/done',
      method: 'POST',
    })
  }

  public async payForPathwayEvent({ eventId, stepNumber }: { eventId: string, stepNumber: number }) {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: '/game/actions/pathway-events/pay',
      method: 'POST',
      body: {
        eventId,
        stepNumber,
      },
    })
  }

  public async payForExchangeItem({ itemId }: { itemId: string }) {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: '/game/actions/exchange/pay',
      method: 'POST',
      body: {
        itemId,
      },
    })
  }

  public async payForRewardLiveOp({ eventId, stepNumber }: { eventId: number, stepNumber: number }) {
    return await this.fetch<{ playerStats: PlayerStats, success: boolean }>({
      path: '/game/actions/reward-live-ops/pay',
      method: 'POST',
      body: {
        eventId,
        stepNumber,
      },
    })
  }

  public async claimRewardLiveOp({ eventId }: { eventId: number }) {
    return await this.fetch<{ playerStats: PlayerStats, success: boolean }>({
      path: '/game/actions/reward-live-ops/claim-reward',
      method: 'POST',
      body: {
        eventId,
      },
    })
  }

  public async sync(isInit: boolean, utmSource: Nullable<string>) {
    return await this.fetch<{
      playerStats: PlayerStats
      offlineProfit: number
      offlineProfitHours: number
      wasDormant?: boolean
      rewards: { coins: number, spins: number, tphX2: number, tickets: number }
      notifications: Notification[]
      reauth?: boolean
      minesCount?: number
      isInStarReset: boolean
      dropeeBalanceCurrent: number
    }>({
      path: '/game/sync',
      method: 'POST',
      body: {
        initialSync: isInit,
        utmSource: isInit ? utmSource : null,
      },
    })
  }

  public async spin({ multiplier, version }: { multiplier: number, version: number }) {
    return await this.fetch<{ prize: null } | {
      prize: FortuneWheelPrize,
      playerStats: PlayerStats,
      fortuneWheelPlayerState: PlayerFortuneWheelState
      prizeMultiplier: number
    }>({
      path: '/game/actions/fortune-wheel/spin',
      method: 'POST',
      body: {
        version: version,
        multiplier: multiplier,
      },
    })
  }

  public async getGiveawayTickets() {
    return await this.fetch<{ player: number, total: number }>({
      path: '/game/tickets',
      method: 'GET',
    })
  }

  public async getPreviousGiveawayTickets() {
    return await this.fetch<{ player: number, total: number, previousGiveaway: GiveawayConfig | null }>({
      path: '/game/tickets/previous',
      method: 'GET',
    })
  }

  public async spinV2({ multiplier }: { multiplier: number }) {
    return await this.fetch<{ prize: null } | {
      prize: FortuneWheelPrizeV2,
      playerStats: PlayerStats,
      fortuneWheelPlayerState: PlayerFortuneWheelState
      prizeMultiplier: number
      gameConfig: PlayerConfig['game']
    }>({
      path: '/game/actions/fortune-wheel/spin-v2',
      method: 'POST',
      body: { multiplier },
    })
  }

  public async notifySpin(utmSource: string) {
    await this.fetch({
      path: '/game/notify-spin',
      method: 'POST',
      body: {
        utmSource,
      },
    })
  }

  public async convertUsdtToStars() {
    return await this.fetch<{ playerState: PlayerStats, fortuneWheelState: PlayerFortuneWheelState }>({
      path: '/game/convert-usdt-to-stars',
      method: 'POST',
    })
  }

  public async getAchievements() {
    return await this.fetch<{
      stats: {
        cardsUpgrade: number
        cardsAbove15: number
        cardsAbove25: number
        cardsAbove50: number
        cardsAbove100: number
        cardsAbove150: number
        specialCards: number
        friends: number
        tonTxCount: number
        tonDailyCheckin: number
        dappRadarTtm: number
        dailyCheckinStreaks: number
        tasks: number
        spins: number
        badges: number
        daysSinceOnboarding: number
        pathwayCompletionsCount: number
        pathwayStepCompletionsCount: number
        revealedMysteryBoxes: number
        dailyCombo: number
      },
    }>({
      path: '/game/achievements',
      method: 'GET',
    })
  }

  public async getAchievementsV1() {
    return await this.fetch<AchievementsV1Response | { success: boolean, message: string }>({
      path: '/game/achievements-v1',
      method: 'GET',
    })
  }

  public async getCurrentLevelRank() {
    return await this.fetch<{
      rank: number | null
      effortLeaderboardRank: number
    }>({
      path: '/game/current-level-rank',
      method: 'GET',
    })
  }

  public async reportError(input: { description: string, code: string, metadata: object }) {
    await this.fetch<{ prize: null } | {
      prize: FortuneWheelPrize,
      playerStats: PlayerStats,
      fortuneWheelPlayerState: PlayerFortuneWheelState
    }>({
      path: '/game/error',
      method: 'POST',
      body: input,
    })
  }

  public async playPvp(strategy: PVP_GAME_STRATEGY) {
    return await this.fetch<{ matchId: string }>({
      path: '/pvp/play',
      method: 'POST',
      body: {
        strategy,
      },
    })
  }

  public async getPvpMatch(matchId: string) {
    return await this.fetch<{ match: Nullable<{ status: PVP_GAME_STATUS }>, playerStats: Nullable<PlayerStats> }>({
      path: `/pvp/matches/${matchId}`,
      method: 'GET',
    })
  }

  public async resolveInvoiceByTx({ nonce, txHash }: { nonce: string, txHash: string }) {
    return await this.fetch<{ match: Nullable<{ status: PVP_GAME_STATUS }>, playerStats: Nullable<PlayerStats> }>({
      path: `/ecommerce/resolve-by-tx`,
      method: 'POST',
      body: {
        nonce,
        txHash,
        isAccountAbstraction: true,
      },
    })
  }

  public async getJettons(walletAddress: string) {
    return await this.fetch<{
      balances: { name: string, symbol: string, balance: string, image: string, decimals: number }[]
    }>({
      path: `/ton/jettons`,
      method: 'POST',
      body: {
        walletAddress,
      },
    })
  }

  public async getFortuneWheelState() {
    return await this.fetch<{ state: PlayerFortuneWheelState }>({
      path: '/game/fortune-wheel',
      method: 'GET',
    })
  }

  public async cipher(decipheredLetters: string, code: string) {
    return await this.fetch<{ decipheredLetters: string } & ({ successful: false } | {
      successful: true,
      playerStats: PlayerStats
    })>({
        path: '/game/actions/cipher',
        method: 'POST',
        body: {
          decipheredLetters,
          code,
        },
      })
  }

  public async claimPurchaseReward(minAmountCents: number) {
    return await this.fetch<{ successful: false } | {
      successful: true,
      playerStats: PlayerStats
      fortuneWheelState: PlayerFortuneWheelState
    }>({
      path: '/game/actions/purchase-reward/claim',
      method: 'POST',
      body: {
        minAmountCents,
      },
    })
  }

  public async claimTonTxCountReward(minTxCount: number) {
    return await this.fetch<{ successful: false } | {
      successful: true,
      playerStats: PlayerStats
      fortuneWheelState: PlayerFortuneWheelState
    }>({
      path: '/game/actions/ton-tx-count-reward/claim',
      method: 'POST',
      body: {
        minTxCount,
      },
    })
  }

  public async claimPathwayCountReward(minCount: number) {
    return await this.fetch<{ successful: false } | {
      successful: true,
      playerStats: PlayerStats
      fortuneWheelState: PlayerFortuneWheelState
    }>({
      path: '/game/actions/pathway-count-reward/claim',
      method: 'POST',
      body: {
        minCount,
      },
    })
  }

  public async cipherConfig() {
    return await this.fetch<{ cipherSize: number[] }>({
      path: '/game/cipher',
      method: 'GET',
    })
  }

  public async setToken(token: string) {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: '/game/actions/token',
      method: 'POST',
      body: {
        token,
      },
    })
  }

  public async tap(input: {
    count: number,
    startTimestamp: number,
    duration: number,
    availableEnergy: number
  }) {
    return await this.fetch<{ coins: number, availableEnergy: number, activities: PlayerActivities }>({
      path: '/game/actions/tap',
      method: 'POST',
      body: {
        count: input.count,
        startTimestamp: input.startTimestamp,
        duration: input.duration,
        availableEnergy: input.availableEnergy,
      },
    })
  }

  public async useBooster(): Promise<{ playerStats: PlayerStats }> {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: `/game/actions/use-booster`,
      method: 'POST',
      body: {
        boosterId: 'refill',
      },
    })
  }

  public async deleteAccount(): Promise<void> {
    await this.fetch({
      path: `/game/me`,
      method: 'DELETE',
    })
  }

  public async requestTransfer() {
    return await this.fetch<{ success: boolean, message: string, reload?: boolean }>({
      path: `/game/actions/request-transfer`,
      method: 'POST',
    })
  }

  public async getLeaderboard(level: number) {
    return await this.fetch<{
      maxVisibleRank: number
      maxListLength: number
      thisPlayer: { rank: Nullable<number>, isInList: boolean, isInThisLevel: boolean }
      players: { id: string, firstName: string, profit: number, photoUrl: string, isThisPlayer: boolean, isPremium: boolean }[]
    }>({
      path: `/game/leaderboard`,
      method: 'POST',
      body: {
        level: level,
      },
    })
  }

  public async getEffortLeaderboard() {
    return await this.fetch<{
      thisPlayer: { rank: number, score: number }
      players: { firstName: string, score: number, photoUrl: string, isPremium: boolean, isThisPlayer: boolean }[]
    }>({
      path: `/game/effort-leaderboard`,
      method: 'POST',
    })
  }

  public async getCardUpgradeLeaderboard(type: 'daily' | 'weekly' | 'allTime') {
    return await this.fetch<LeaderboardResponse>({
      path: `/game/leaderboard/card-upgrade`,
      method: 'POST',
      body: {
        type,
      },
    })
  }

  public async simpleActionCompleted(action: string) {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: `/game/actions/simple-action`,
      method: 'POST',
      body: {
        action,
      },
    })
  }

  public async getSpinForAd() {
    return await this.fetch<{ playerStats: PlayerStats, fortuneWheelState: PlayerFortuneWheelState }>({
      path: `/game/actions/extra-spin-by-ad`,
      method: 'POST',
    })
  }

  public async getDoublePrizeForAd({ isAutoSpin }: { isAutoSpin: boolean }) {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: `/game/actions/double-prize-by-ad`,
      method: 'POST',
      body: {
        isAutoSpin,
      },
    })
  }

  public async multiplyOfflineProfitForAd() {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: `/game/actions/multiply-offline-profit-for-ad`,
      method: 'POST',
    })
  }

  public async getEcommerceInvoiceUrl({ itemId, priceType, metadata }: {
    itemId: string
    priceType?: ECOMMERCE_ITEM_PRICE_TYPE
    metadata?: PurchaseMetadata | undefined
  }) {
    return await this.fetch<{ isPaid: false, invoiceUrl: string, nonce: string, amount: number } | { isPaid: true, nonce: string }>({
      path: '/ecommerce/invoice-url',
      method: 'POST',
      body: {
        itemId: itemId,
        priceType: priceType ?? ECOMMERCE_ITEM_PRICE_TYPE.STAR,
        metadata,
      },
    })
  }

  public async getCardInvoiceUrl({ upgradeId, itemId, priceType }: {
    upgradeId: string
    itemId: SPECIAL_ECOMMERCE_ITEM_ID.CARD_FAST_FORWARD | SPECIAL_ECOMMERCE_ITEM_ID.CARD_UPGRADE
    priceType: ECOMMERCE_ITEM_PRICE_TYPE
  }) {
    return await this.fetch<{ isPaid: false, invoiceUrl: string, nonce: string, amount: number } | { isPaid: true, nonce: string }>({
      path: '/ecommerce/invoice-url/card',
      method: 'POST',
      body: {
        upgradeId,
        priceType: priceType,
        ecommerceItemId: itemId,
      },
    })
  }

  public async getBatchCardFastForwardInvoiceUrl({ priceType }: {
    priceType: ECOMMERCE_ITEM_PRICE_TYPE
  }) {
    return await this.fetch<{ isPaid: false, invoiceUrl: string, nonce: string, amount: number } | { isPaid: true, nonce: string }>({
      path: '/ecommerce/invoice-url/batch-fast-forward',
      method: 'POST',
      body: {
        priceType: priceType,
      },
    })
  }

  public async getPurchaseStatus({ nonce }: {
    nonce: string
  }) {
    return await this.fetch<{ status: 'completed' | 'pending' }>({
      path: '/ecommerce/purchase-status',
      method: 'POST',
      body: {
        nonce,
      },
    })
  }

  public async openMysteryBox({ boxType }: {
    boxType: MYSTERY_BOX_TYPE
  }) {
    return await this.fetch<{ rewards: MysteryBoxReward[], playerStats: PlayerStats }>({
      path: '/game/actions/mystery-box/open',
      method: 'POST',
      body: {
        boxType,
      },
    })
  }

  public async increaseStars() {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: '/game/get-free-stars',
      method: 'POST',
    })
  }

  public async updateNotificationSettings(type: SettingsNotificationType, isEnabled: boolean) {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: '/game/settings/notification',
      method: 'POST',
      body: {
        type,
        isEnabled,
      },
    })
  }

  public async setWalletAddress(type: 'ton', address: Nullable<string>) {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: '/game/wallet',
      method: 'POST',
      body: {
        type,
        address,
      },
    })
  }

  public async getFriends() {
    return await this.fetch<{
      friends: Friend[]
      pokes: string[]
      virtualFriendIdPrefix: string
      friendsCount: number
      referralsCount: number
    }>({
      path: `/game/friends-v2`,
      method: 'GET',
    })
  }

  public async useCollectible({ collectibleId }: { collectibleId: string }) {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: `/game/use-collectible`,
      method: 'POST',
      body: {
        collectibleId,
      },
    })
  }

  public async addFriend(referrerCode: string) {
    return await this.fetch({
      path: `/game/friends`,
      method: 'POST',
      body: {
        referrerCode,
      },
    })
  }

  public async pokeFriend(id: string) {
    return await this.fetch<{ success: boolean, pokedFriends: number[], activities: PlayerActivities }>({
      path: `/game/actions/friends/${id}/poke-v2`,
      method: 'POST',
    })
  }

  public async getPlayerByReferralCode(referralCode: string) {
    return await this.fetch<{ player: Nullable<{ firstName: string, samePlayer: boolean }> }>({
      path: `/game/player-by-referral-code`,
      method: 'POST',
      body: {
        referralCode,
      },
    })
  }

  public async checkin(purchaseNonce?: string): Promise<{ playerStats: PlayerStats }> {
    return await this.fetch<{ playerStats: PlayerStats }>({
      path: `/game/actions/tasks/daily-checkin?v=2`,
      method: 'POST',
      body: {
        purchaseNonce,
      },
    })
  }

  public async doTaskAction(taskId: string): Promise<{ config: PlayerConfig, playerStats: PlayerStats }> {
    return await this.fetch<{ config: PlayerConfig, playerStats: PlayerStats }>({
      path: `/game/actions/tasks/action-completed`,
      method: 'POST',
      body: {
        taskId: taskId,
      },
    })
  }

  public async doTask(taskId: string) {
    return await this.fetch<{ config: PlayerConfig, playerStats: PlayerStats, error?: 'not_in_tg_channel' }>({
      path: `/game/actions/tasks/done`,
      method: 'POST',
      body: {
        taskId: taskId,
      },
    })
  }

  public async claimTask(taskId: DAILY_TASK_ID): Promise<{ playerStats: PlayerStats, success: boolean }> {
    return await this.fetch<{ playerStats: PlayerStats, success: boolean }>({
      path: `/game/actions/tasks/daily/done`,
      method: 'POST',
      body: {
        taskId: taskId,
      },
    })
  }

  public async answerDailyQuestion(answer: string) {
    return await this.fetch<{ success: boolean, playerStats: PlayerStats }>({
      path: `/game/actions/tasks/daily-question/answer`,
      method: 'POST',
      body: {
        answer: answer,
      },
    })
  }

  public async getDailyQuestion() {
    return await this.fetch<{ question: string }>({
      path: `/game/daily-question`,
      method: 'GET',
    })
  }

  public async upgrade(upgradeId: string) {
    return await this.fetch<{
      playerStats: PlayerStats
      config: PlayerConfig
      badges: Badge[]
      event?: UpgradeEvent
      activities: PlayerActivities
    }>({
      path: `/game/actions/upgrade`,
      method: 'POST',
      body: {
        upgradeId,
      },
    })
  }

  public async upgradeAllCardsWithGoldenCoins() {
    return await this.fetch<{
      playerStats: PlayerStats
      config: PlayerConfig
      badges: Badge[]
      activities: PlayerActivities
      summary: {
        upgradedCards: Array<{ upgradeId: string, level: number, cost: number, profitGain: number }>
        totalCoinsSpent: number
        totalProfitGained: number
        skippedCards: Array<{ upgradeId: string, reason: string }>
      }
    }>({
      path: '/game/actions/upgrade-all',
      method: 'POST',
    })
  }

  public async getConfig(): Promise<{ config: PlayerConfig, playerStats: PlayerStats }> {
    return await this.fetch<{ config: PlayerConfig, playerStats: PlayerStats }>({
      path: '/game/config',
      method: 'GET',
    })
  }

  public async getBadgesV2() {
    return await this.fetch<{ badges: Partial<Record<BADGE_ID, number>> }>({
      path: '/game/badges-v2',
      method: 'GET',
    })
  }

  public async withdrawDropee({ walletAddress }: { walletAddress: string }) {
    return await this.fetch({
      path: '/game/dropee/withdraw',
      method: 'POST',
      body: {
        walletAddress: walletAddress,
      },
    })
  }

  public async submitRaffleData(input: { username: string, walletAddress: string }) {
    await this.fetch({
      path: '/game/raffle/submit',
      method: 'POST',
      body: input,
    })
  }

  public async getRaffleSubmissionData() {
    return await this.fetch<{ data: Nullable<{ username: string, walletAddress: string }> }>({
      path: '/game/raffle/my-submission',
      method: 'GET',
    })
  }

  public async checkWonRaffle() {
    return await this.fetch<{ position: Nullable<number> }>({
      path: '/game/raffle/check',
      method: 'GET',
    })
  }

  public async getRaffleWinners() {
    return await this.fetch<{
      currentGiveawayWinnersDrawn: boolean,
      winners: { firstName: string, photoUrl: string | null }[]
      position: Nullable<number>
    }>({
      path: '/game/raffle/winners',
      method: 'GET',
    })
  }

  public async getPathwayLeaderboard() {
    return await this.fetch<LeaderboardResponse>({
      path: '/game/pathway-leaderboard',
      method: 'GET',
    })
  }

  public async getPuzzleChallengeLeaderboard() {
    return await this.fetch<LeaderboardResponse>({
      path: '/game/puzzle-challenge-leaderboard',
      method: 'GET',
    })
  }

  public async getPuzzlesCompletionChallengeLeaderboard() {
    return await this.fetch<LeaderboardResponse>({
      path: '/game/puzzles-completion-challenge-leaderboard',
      method: 'GET',
    })
  }

  public async getMemoryGameChallengeLeaderboard() {
    return await this.fetch<LeaderboardResponse>({
      path: '/game/memory-game-challenge-leaderboard',
      method: 'GET',
    })
  }

  public async getMinesweeperGameChallengeLeaderboard() {
    return await this.fetch<LeaderboardResponse>({
      path: '/game/minesweeper-challenge-leaderboard',
      method: 'GET',
    })
  }

  public async getSpecialCollectibleChallengeLeaderboard() {
    return await this.fetch<LeaderboardResponse>({
      path: '/game/special-collectible-challenge-leaderboard',
      method: 'GET',
    })
  }

  public async buyMiniGameEntry(input: { gameId: MINI_GAME_ID }) {
    return await this.fetch<{ playerStats: PlayerStats, error?: string, nonce?: string }>({
      path: '/game/actions/mini-games/buy-entry',
      method: 'POST',
      body: input,
    })
  }

  public async submitMiniGameDraftScore(input: { gameId: MINI_GAME_ID, nonce: string, score: number, durationMillis: number }) {
    return await this.fetch<{ error?: string }>({
      path: '/game/actions/mini-games/submit-draft',
      method: 'POST',
      body: input,
    })
  }

  public async submitMiniGameScore(input: { gameId: MINI_GAME_ID, nonce: string, score: number, durationMillis: number }) {
    return await this.fetch<{ playerStats: PlayerStats, error?: string }>({
      path: '/game/actions/mini-games/submit-score',
      method: 'POST',
      body: input,
    })
  }

  public async getV1Stars() {
    return await this.fetch<{ stars: number, starsFromUsdt: number, usdtCentsBalance: number }>({
      path: '/game/v1-stars',
      method: 'GET',
    })
  }

  public async claimV1Stars() {
    await this.fetch({
      path: '/game/v1-stars-claim',
      method: 'POST',
    })
  }

  public async liveOpsGetUserEvents() {
    return await this.fetch<{ currencies: LiveOpsCurrency[], eventTemplates: LiveOpsEventTemplate[], userEvents: LiveOpsUserEvent[] }>({
      path: '/competition/_/live-ops/user-events',
      useDropeeApps: true,
      method: 'POST',
    })
  }

  public async getLeaderboards() {
    return await this.fetch<{ leaderboards: LiveOpsLeaderboard[] }>({
      path: '/competition/_/live-ops/leaderboards',
      useDropeeApps: true,
      method: 'GET',
    })
  }

  public async getLiveOpsLeaderboard(leaderboardId: string) {
    return await this.fetch<LiveOpsLeaderboardResponse>({
      path: `/competition/_/live-ops/leaderboards/${leaderboardId}`,
      useDropeeApps: true,
      method: 'POST',
    })
  }

  public async liveOpsDoUserEventAction({ userEventId, action }: { userEventId: string, action: Record<string, unknown> }) {
    return await this.fetch<{ success: true } | {
      success: false,
      paymentCurrencies: { ton: string, star: string },
      paymentInfo: { itemId: string, itemName: string, usdAmount: number, rewards: CurrencyAmount[] }
    }>({
      path: '/competition/_/live-ops/user-event-action',
      useDropeeApps: true,
      method: 'POST',
      body: {
        action: action,
        userEventId: userEventId,
      },
    })
  }

  public async liveOpsGenerateInvoice({ userEventId, action, currency }: {
    userEventId: string,
    action: Record<string, unknown>,
    currency: string
  }): Promise<InvoiceResponse> {
    const result = await this.fetch<{ isPaid: true } | { isPaid: false, invoiceId: string, invoiceUrl: string, amount: number }>({
      path: '/competition/_/live-ops/generate-invoice',
      useDropeeApps: true,
      method: 'POST',
      body: {
        action: action,
        userEventId: userEventId,
        currency: currency,
      },
    })

    return result.isPaid ? {
      isPaid: true,
    } : {
      isPaid: false,
      nonce: result.invoiceId,
      amount: result.amount,
      invoiceUrl: result.invoiceUrl,
    }
  }

  public async multiplayerTargetAction(input: { action: { type: string, count?: number } }) {
    return await this.fetch<{ success: boolean, targetedPlayers?: { firstName: string, photoUrl: string }[], playerStats: PlayerStats }>({
      path: '/game/multiplayer/target-action',
      method: 'POST',
      body: input,
    })
  }

  public async multiplayerScanForMines() {
    return await this.fetch<{ success: boolean, acquiredMinesCount: number, playerStats: PlayerStats }>({
      path: '/game/multiplayer/mine/scan',
      method: 'POST',
    })
  }

  public async trackEvent({ event, metadata }: { event: string, metadata: Record<string, string | number | boolean> }) {
    await this.fetch({
      path: '/game/track',
      method: 'POST',
      body: {
        event,
        metadata,
      },
    })
  }
}

export default new Api()
